import classnames from 'classnames';
import { useLocation } from 'react-router';
import { analytics, hasUserNotification, useUser, userNotificationPathnames } from 'libs';
import {
  AwardIcon,
  BookmarkIcon,
  BuildingsIcon,
  BullhornIcon,
  CreditCardIcon,
  FileIcon,
  FileTrayFullIcon,
  FinmoLogoIcon,
  GearIcon,
  LandmarkArrowsIcon,
  LandmarkIcon,
  LockIcon,
  MemoIcon,
  MicrochipAiIcon,
  QuestionCircleIcon,
  ScaleUnbalancedIcon,
  SignOutIcon,
  SparkIcon,
  StarIcon,
  TagAltIcon,
  UserCircleIcon,
  UserFriendsIcon,
  UserIcon,
  WaveTriangleIcon,
} from 'icons';
import Hamburger from './Hamburger';
import MobileNavGroup from './MobileNavGroup';
import MobileNavItem from './MobileNavItem';
import Nav from './Nav';

interface MobileNavProps {
  canManageSubscriptions: boolean;
  hasBrokerage: boolean;
  hasPaymentMethod?: boolean;
  isOpen: boolean;
  onToggle: () => void;
}

const MobileNav = ({ canManageSubscriptions, hasBrokerage, hasPaymentMethod, isOpen, onToggle }: MobileNavProps) => {
  const { pathname } = useLocation();
  const user = useUser();
  const hasTeamDocumentsNotification = hasUserNotification(user, userNotificationPathnames.teamDocuments);
  const hasTeamAnnouncementsNotification = hasUserNotification(user, userNotificationPathnames.teamAnnouncements);

  return (
    <div className={classnames('mobile-nav', { open: isOpen })}>
      <Hamburger onToggle={onToggle} />
      {isOpen && (
        <Nav className="mobile-nav__menu">
          <div className="mobile-nav__links">
            <div className="mobile-nav__navlinks">
              <MobileNavItem
                icon={<WaveTriangleIcon />}
                isActive={pathname.startsWith('/rates')}
                label="Rates"
                onClick={analytics.navigation.onMobile('rates')}
                testId="mobile-nav-navlink-rates"
                to="/rates"
              />
              <MobileNavItem
                icon={<ScaleUnbalancedIcon />}
                isActive={pathname.startsWith('/scenarios')}
                label="Scenarios"
                onClick={analytics.navigation.onMobile('scenarios')}
                testId="mobile-nav-navlink-scenarios"
                to="/scenarios/comparisons"
              />
              <MobileNavItem
                icon={<LandmarkIcon />}
                isActive={pathname.startsWith('/lenders')}
                label="Lenders"
                onClick={analytics.navigation.onMobile('lenders')}
                testId="mobile-nav-navlink-lenders"
                to="/lenders"
              />
              <MobileNavItem
                icon={<FileTrayFullIcon />}
                isActive={pathname.startsWith('/guidelines')}
                label="Policies"
                onClick={analytics.navigation.onMobile('guidelines')}
                testId="mobile-nav-navlink-guidelines"
                to="/guidelines"
              />
              <MobileNavItem
                icon={<LandmarkArrowsIcon />}
                isActive={pathname.startsWith('/alternative-lending')}
                label="Alternative Lending"
                onClick={analytics.navigation.onMobile('alternative-lending')}
                testId="mobile-nav-navlink-alternative-lending"
                to="/alternative-lending"
              />
              <MobileNavItem
                icon={<MicrochipAiIcon />}
                isActive={pathname === '/ai'}
                label="AI Assistant"
                onClick={analytics.navigation.onMobile('ai')}
                testId="mobile-nav-navlink-ai"
                to="/ai"
              />
              {user.features.laravel_ai && (
                <MobileNavItem
                  icon={<SparkIcon />}
                  isActive={pathname.startsWith('/ai/conversations')}
                  label="Spotlight Spark"
                  onClick={analytics.navigation.onMobile('conversations')}
                  testId="mobile-nav-navlink-conversations"
                  to="/ai/conversations"
                />
              )}
              {hasBrokerage && (
                <MobileNavGroup
                  hasNotification={hasTeamAnnouncementsNotification || hasTeamDocumentsNotification}
                  icon={<UserFriendsIcon />}
                  name="Team"
                  startExpanded={pathname.startsWith('/teams')}
                >
                  <MobileNavItem
                    icon={<BuildingsIcon />}
                    isActive={pathname === '/teams'}
                    label="Information"
                    onClick={analytics.navigation.onMobile('teams')}
                    testId="mobile-nav-navlink-teams"
                    to="/teams"
                  />
                  <MobileNavItem
                    hasNotification={hasTeamDocumentsNotification}
                    icon={<FileIcon />}
                    isActive={pathname === '/teams/documents'}
                    label="Documents"
                    onClick={analytics.navigation.onMobile('teams-documents')}
                    testId="mobile-nav-navlink-teams-documents"
                    to="/teams/documents"
                  />
                  <MobileNavItem
                    hasNotification={hasTeamAnnouncementsNotification}
                    icon={<BullhornIcon />}
                    isActive={pathname === '/teams/announcements'}
                    label="Announcements"
                    onClick={analytics.navigation.onMobile('teams-announcements')}
                    to="/teams/announcements"
                  />
                </MobileNavGroup>
              )}
              {user.features.is_brokerage_admin && (
                <MobileNavGroup
                  icon={<GearIcon />}
                  name="Team Management"
                  startExpanded={pathname.startsWith('/team-management')}
                >
                  <MobileNavItem
                    icon={<BuildingsIcon />}
                    isActive={pathname === `/team-management`}
                    label="Team"
                    onClick={analytics.navigation.onMobile('team-management')}
                    testId="mobile-nav-navlink-team-management"
                    to="/team-management"
                  />
                  <MobileNavItem
                    icon={<UserFriendsIcon />}
                    isActive={pathname === `/team-management/members`}
                    label="Members"
                    onClick={analytics.navigation.onMobile('team-management-members')}
                    testId="mobile-nav-navlink-team-management-members"
                    to="/team-management/members"
                  />
                  <MobileNavItem
                    icon={<FileIcon />}
                    isActive={pathname === `/team-management/documents`}
                    label="Documents"
                    onClick={analytics.navigation.onMobile('team-management-documents')}
                    testId="mobile-nav-navlink-team-management-documents"
                    to="/team-management/documents"
                  />
                  <MobileNavItem
                    icon={<BullhornIcon />}
                    isActive={pathname === '/team-management/announcements'}
                    label="Announcements"
                    onClick={analytics.navigation.onMobile('team-management-announcements')}
                    to="/team-management/announcements"
                  />
                  <MobileNavItem
                    icon={<BookmarkIcon />}
                    isActive={pathname === `/team-management/lender-contacts`}
                    label="Contacts"
                    onClick={analytics.navigation.onMobile('team-management-lender-contacts')}
                    testId="mobile-nav-navlink-team-management-contacts"
                    to="/team-management/lender-contacts"
                  />
                  <MobileNavItem
                    icon={<TagAltIcon />}
                    isActive={pathname === `/team-management/exclusive-rates`}
                    label="Exclusive rates"
                    onClick={analytics.navigation.onMobile('team-management-exclusive-rates')}
                    testId="mobile-nav-navlink-team-management-exclusive-rates"
                    to="/team-management/exclusive-rates"
                  />
                  <MobileNavItem
                    icon={<StarIcon />}
                    isActive={pathname === `/team-management/lender-statuses`}
                    label="Statuses"
                    onClick={analytics.navigation.onMobile('team-management-lender-statuses')}
                    testId="mobile-nav-navlink-team-management-statuses"
                    to="/team-management/lender-statuses"
                  />
                  {user.features.brokerage_notes_and_tags && (
                    <MobileNavItem
                      icon={<MemoIcon />}
                      isActive={pathname === '/team-management/notes-and-tags'}
                      label="Notes and tags"
                      onClick={analytics.navigation.onMobile('team-management-notes-and-tags')}
                      testId="mobile-nav-navlink-team-management-notes-and-tags"
                      to="/team-management/notes-and-tags"
                    />
                  )}
                </MobileNavGroup>
              )}
              <MobileNavItem
                icon={<QuestionCircleIcon />}
                isExternalRoute
                label="Help Centre"
                onClick={analytics.navigation.onMobile('help-centre')}
                shouldOpenNewWindow
                testId="mobile-nav-navlink-help-centre"
                to="https://support.lenderspotlight.ca"
              />
            </div>
            <div className="mobile-nav__settings">
              <MobileNavItem
                icon={<FinmoLogoIcon />}
                isExternalRoute
                label="Switch to Finmo"
                onClick={analytics.navigation.onMobile('finmo')}
                shouldOpenNewWindow
                testId="mobile-nav-navlink-finmo"
                to="https://app.finmo.ca"
              />
              <MobileNavGroup icon={<UserCircleIcon />} name="settings" startExpanded={pathname.includes('settings')}>
                <MobileNavItem
                  icon={<UserIcon />}
                  isActive={pathname === '/settings'}
                  label="Your account"
                  onClick={analytics.navigation.onMobile('settings')}
                  testId="mobile-nav-navlink-settings"
                  to="/settings"
                />
                <MobileNavItem
                  icon={<SignOutIcon />}
                  isForm
                  label="Sign out"
                  onClick={analytics.navigation.onMobile('logout')}
                  testId="mobile-nav-navlink-logout"
                  to="/logout"
                />
                {!user.features.is_brokerage_admin && (
                  <MobileNavItem
                    icon={<BuildingsIcon />}
                    isActive={pathname === '/settings/team-status'}
                    label="Team status"
                    onClick={analytics.navigation.onMobile('team-status')}
                    testId="mobile-nav-navlink-team-status"
                    to="/settings/team-status"
                  />
                )}
                {canManageSubscriptions ? (
                  <MobileNavItem
                    icon={<AwardIcon />}
                    isActive={pathname.startsWith('/settings/subscriptions')}
                    label="Subscriptions"
                    onClick={analytics.navigation.onMobile('subscriptions')}
                    testId="mobile-nav-navlink-subscriptions"
                    to="/settings/subscriptions"
                  />
                ) : (
                  <MobileNavItem
                    icon={<AwardIcon />}
                    isActive={pathname.startsWith('/settings/upgrade')}
                    label="Upgrade"
                    onClick={analytics.navigation.onMobile('upgrade')}
                    testId="mobile-nav-navlink-upgrade"
                    to="/settings/upgrade"
                  />
                )}
                {hasPaymentMethod && (
                  <MobileNavItem
                    icon={<CreditCardIcon />}
                    isActive={pathname.startsWith('/settings/payment')}
                    label="Payment method"
                    onClick={analytics.navigation.onMobile('payment')}
                    testId="mobile-nav-navlink-payment"
                    to="/settings/payment-method"
                  />
                )}
                <MobileNavItem
                  icon={<LockIcon />}
                  isExternalRoute
                  label="Terms of service"
                  onClick={analytics.navigation.onMobile('terms-of-service')}
                  shouldOpenNewWindow
                  testId="mobile-nav-navlink-terms-of-service"
                  to="https://lendesk.com/terms-spotlight"
                />
                <MobileNavItem
                  icon={<LockIcon />}
                  isExternalRoute
                  label="Privacy policy"
                  onClick={analytics.navigation.onMobile('privacy-policy')}
                  shouldOpenNewWindow
                  testId="mobile-nav-navlink-privacy-policy"
                  to="https://lendesk.com/privacy-lendesk"
                />
              </MobileNavGroup>
            </div>
          </div>
        </Nav>
      )}
    </div>
  );
};

export default MobileNav;
